import { useCallback, useRef, useState } from "react";
import { UploadCloud, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { ZoomCanvas } from "@/components/height-map/ZoomCanvas";

export const DropZone = ({
  onFile,
  hasImage,
  imageMeta,
  originalCanvasRef,
  colorCanvasRef,
  showOnlyColor = false,
  bigViewports = false,
  roundedMapPreview = false,
  onLoadSample,
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const smallViewportClass = bigViewports ? "min-h-[520px]" : "min-h-[280px]";
  const smallCanvasClass = bigViewports ? "max-h-[680px]" : "max-h-[420px]";
  const colorViewportClass = bigViewports ? "min-h-[680px]" : "min-h-[520px]";
  const colorCanvasClass = bigViewports ? "max-h-[720px]" : "max-h-[560px]";

  const handleFiles = useCallback(
    (files) => {
      if (!files || files.length === 0) return;
      const file = files[0];
      if (!file.type.startsWith("image/")) {
        toast.error("Please drop an image file (PNG / JPG / WEBP).");
        return;
      }
      onFile(file);
    },
    [onFile],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  if (hasImage) {
    if (showOnlyColor) {
      return (
        <div className="rounded-2xl border border-zinc-800/80 bg-[#101116] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800/60">
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <span className="w-2.5 h-2.5 rounded-full brand-gradient" />
              <span className="uppercase tracking-wider">Generated Color Map</span>
            </div>
            <div className="flex items-center gap-3">
              {imageMeta && (
                <span className="text-[11px] text-zinc-500 mono">
                  {imageMeta.width}×{imageMeta.height}
                </span>
              )}
              <button
                data-testid="replace-image-btn"
                onClick={() => inputRef.current?.click()}
                className="text-[11px] text-zinc-400 hover:text-zinc-100 transition"
              >
                Replace image
              </button>
            </div>
          </div>
          <ZoomCanvas testId="color-zoom" className={colorViewportClass}>
            <canvas
              data-testid="color-canvas"
              ref={colorCanvasRef}
              className={`max-w-full ${colorCanvasClass} object-contain ${roundedMapPreview ? "rounded-xl" : ""}`}
              style={{ imageRendering: "pixelated" }}
            />
          </ZoomCanvas>
          <input
            ref={inputRef}
            data-testid="file-input-hidden"
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-zinc-800/80 bg-[#101116] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800/60">
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <ImageIcon className="w-3.5 h-3.5" />
              <span className="uppercase tracking-wider">Original Height Map</span>
            </div>
            <div className="flex items-center gap-3">
              {imageMeta && (
                <span className="text-[11px] text-zinc-500 mono">
                  {imageMeta.width}×{imageMeta.height}
                </span>
              )}
              <button
                data-testid="replace-image-btn"
                onClick={() => inputRef.current?.click()}
                className="text-[11px] text-zinc-400 hover:text-zinc-100 transition"
              >
                Replace image
              </button>
            </div>
          </div>
          <ZoomCanvas testId="original-zoom" className={smallViewportClass}>
            <canvas
              data-testid="original-canvas"
              ref={originalCanvasRef}
              className={`max-w-full ${smallCanvasClass} object-contain ${roundedMapPreview ? "rounded-xl" : ""}`}
              style={{ imageRendering: "pixelated" }}
            />
          </ZoomCanvas>
        </div>
        <div className="rounded-2xl border border-zinc-800/80 bg-[#101116] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800/60">
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <span className="w-2.5 h-2.5 rounded-full brand-gradient" />
              <span className="uppercase tracking-wider">Generated Color Map</span>
            </div>
          </div>
          <ZoomCanvas testId="color-zoom" className={smallViewportClass}>
            <canvas
              data-testid="color-canvas"
              ref={colorCanvasRef}
              className={`max-w-full ${smallCanvasClass} object-contain ${roundedMapPreview ? "rounded-xl" : ""}`}
              style={{ imageRendering: "pixelated" }}
            />
          </ZoomCanvas>
        </div>
        <input
          ref={inputRef}
          data-testid="file-input-hidden"
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="drop-zone"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className={`relative dashed-drop rounded-2xl cursor-pointer transition-all duration-200 ${
        dragOver ? "pink-glow scale-[1.005]" : "hover:border-zinc-700"
      }`}
      style={{ minHeight: 460 }}
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
        <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center shadow-lg shadow-rose-500/20">
          <UploadCloud className="w-7 h-7 text-white" strokeWidth={2.2} />
        </div>
        <div>
          <div className="text-base font-medium text-zinc-100">
            Drop a grayscale height map here
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            or click to browse · PNG / JPG / WEBP
          </div>
        </div>
        {onLoadSample && (
          <div
            className="mt-2 flex flex-col items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[11px] uppercase tracking-widest text-zinc-600">
              or try a sample terrain
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {[
                { id: "hills", label: "Rolling Hills" },
                { id: "mountains", label: "Mountains" },
                { id: "island", label: "Island" },
              ].map((s) => (
                <button
                  key={s.id}
                  data-testid={`sample-${s.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onLoadSample(s.id);
                  }}
                  className="rounded-full border border-zinc-700 bg-zinc-900/70 px-3.5 py-1.5 text-xs font-medium text-zinc-200 transition-all duration-200 ease-out hover:-translate-y-px hover:border-rose-400/70 hover:text-white hover:shadow-md hover:shadow-rose-500/15"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        data-testid="file-input"
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
};
